<template>
    <v-card class="half-card overflow-auto" elevation="3">
        <v-card-title>
            <h3>Trip Route Map</h3>
        </v-card-title>
        <v-card-text ref="mapContainer" class="pa-0" style="position: relative;">
            <div style="height: 300px;">
                <l-map ref="map" @ready="onMapReady">
                    <l-tile-layer :url="$mapTiles.street"/>
                </l-map>
            </div>

            <!-- Driver-analytics event timeline panel -->
            <div v-if="events.length" class="da-event-panel" :class="{ 'da-event-panel-open': showEventPanel }">
                <button
                    type="button"
                    class="da-event-panel-toggle"
                    :title="showEventPanel ? 'Hide events' : 'Show events'"
                    @click="showEventPanel = !showEventPanel"
                >
                    <i class="mdi mdi-alert-circle" style="color: #E53935;"/>
                    <span class="da-event-panel-count">{{ events.length }}</span>
                    <i class="mdi" :class="showEventPanel ? 'mdi-chevron-up' : 'mdi-chevron-down'"/>
                </button>
                <div v-if="showEventPanel" class="da-event-panel-list">
                    <div
                        v-for="event in events"
                        :key="event.index"
                        class="da-event-panel-row"
                        @click="focusEvent(event)"
                    >
                        <div class="da-event-panel-row-icons">
                            <i
                                v-for="bit in event.bits"
                                :key="bit.mask"
                                class="mdi"
                                :class="bit.icon"
                                :style="{ color: bit.color }"
                            />
                        </div>
                        <div class="da-event-panel-row-meta">
                            <div class="da-event-panel-row-labels">{{ event.bits.map(b => b.label).join(', ') }}</div>
                            <div class="da-event-panel-row-time">{{ formatDateTime(event.route.event_time) }}</div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Replay controls - directly below map, no gap -->
            <div v-if="routes.length > 1" class="replay-controls">
                <v-btn
                    :icon="isPlaying ? 'mdi-pause' : 'mdi-play'"
                    size="x-small"
                    variant="flat"
                    color="primary"
                    @click="togglePlayback"
                />
                <v-slider
                    v-model="replayIndex"
                    :max="routes.length - 1"
                    :min="0"
                    density="compact"
                    hide-details
                    class="replay-slider"
                    @update:model-value="seekTo"
                />
                <v-btn
                    size="x-small"
                    variant="tonal"
                    class="speed-btn"
                    @click="cycleSpeed"
                >{{ replaySpeed }}x</v-btn>
            </div>
        </v-card-text>
    </v-card>
</template>

<script setup>
import "leaflet/dist/leaflet.css";
import {send} from "@/composables/apiClient";
import {inject, ref, shallowRef, watch, onUnmounted, computed} from "vue";
import {LMap, LTileLayer} from "@vue-leaflet/vue-leaflet";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {strOperations} from "@/composables/globals";
import {useAuthStore} from "@/stores/authStore";
import {formatDateTime} from "@/composables/useTimezone";
import {
    COLOR_DA_EVENT_FALLBACK,
    daEventBitsSet,
    daEventLabels,
    primaryDaEvent,
} from "@/composables/daEvents";

const routes = ref([]);
const markers = ref([]);
const polyLines = ref([]);
const eventBadges = ref([]); // L.marker handles for per-event badges
const events = ref([]); // chronological list for the timeline panel
const showEventPanel = ref(false);
const company = ref(null);
const layerGroup = ref(null);
const map = shallowRef(null);
const selectedTrip = ref(null);
const mapContainer = ref(null);

const {user} = useAuthStore();
const {showSnackbar} = useSnackbarStore();
const lastMqttUpdate = inject('lastMqttUpdate', ref(null));
const props = defineProps(['selectedDevice', 'selectedTrip']);
const mapReady = ref(false);

// --- Replay state ---
const replayIndex = ref(0);
const isPlaying = ref(false);
const replaySpeed = ref(1);
let replayTimer = null;
let replayMarker = null;
const SPEED_OPTIONS = [0.25, 0.5, 1, 2, 4];

const onMapReady = () => {
    map.value.leafletObject.attributionControl.remove();
    mapReady.value = true;
    // If trip was already set before map was ready (e.g. Asset Details), draw now
    if (props.selectedTrip && props.selectedDevice?.asset_id) {
        drawTripRoutes();
    }
};

const drawTripRoutes = async () => {
    resetRoutes();
    selectedTrip.value = props.selectedTrip;
    if (['admin', 'super_admin'].includes(user.type) && company.value?.id !== props.selectedDevice.company) {
        await fetchCompany(props.selectedDevice.company);
    }
    await fetchTripRoutes();
    if (routes.value.length && map.value) {
        setTimeout(() => {
            mapTripRoutes();
            setTimeout(() => animateMarkers(), 100);
        }, 50);
    }
};

const speedThreshold = computed(() => {
    let speed, unit;
    if (['admin', 'super_admin'].includes(user.type)) {
        speed = company.value?.speed_threshold ?? 0
        unit = company.value?.speed_unit ?? 'miles';
    } else {
        speed = user?.company?.speed_threshold ?? 0
        unit = user?.company?.speed_unit ?? 'miles';
    }
    return parseFloat(strOperations().unitifyDistance(speed, 'speed', unit))
});
// Animation control
const animationTimeouts = ref([]);
const currentAnimationId = ref(0);

// Takes the whole route point, not just the raw number, so it can prefer the
// server-converted speed_mph. The raw gps_speed carries MPH from hardware and
// km/h x2 from phones, so colouring off it painted a hardware trip at 31% of
// its real speed - a 112 mph stretch came out green. Falls back to the raw
// value when speed_mph is absent, which is exactly the previous behaviour.
const getStrokeColor = (route) => {
    const mph = strOperations().speedToMph(route);
    const speed = parseFloat(strOperations().unitifyDistance(mph)) || 0;
    if (speedThreshold.value > 0) {
        if (speed >= speedThreshold.value) return '#E53935';
        if (speed >= speedThreshold.value - 10) return '#FB8C00';
        return '#4CAF50';
    }
    if (speed >= 70) return '#E53935';
    if (speed >= 50) return '#FB8C00';
    return '#4CAF50';
}

// Clear all animation timeouts
const clearAnimationTimeouts = () => {
    animationTimeouts.value.forEach(timeout => clearTimeout(timeout));
    animationTimeouts.value = [];
    currentAnimationId.value++;
};

// Force-stop all marker CSS animations and make them visible
const forceRevealAllMarkers = () => {
    clearAnimationTimeouts();
    markers.value.forEach(marker => {
        const el = marker?.getElement();
        if (el) {
            const icon = el.querySelector("i");
            if (icon) {
                icon.classList.remove("opacity-0", "drop");
                icon.style.animation = 'none';
                icon.style.opacity = '1';
            }
        }
    });
};

const fetchCompany = async (companyId) => {
    await send('get', `companies/${companyId}?without_coordinates=1`)
        .then((response) => {
            if (response.data.data.company) {
                company.value = response.data.data.company;
            }
        })
        .catch((error) => {
            console.log(error)
        })
}

const fetchTripRoutes = async () => {
    await send('get', `assets/${props.selectedDevice.asset_id}/trips/${props.selectedTrip.id}/routes`)
        .then((response) => {
            // Assign unconditionally. Keeping the old value on an empty response
            // left the previous trip's polyline drawn under the new trip.
            routes.value = response.data.data || [];
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Trip routes could not be loaded', 'error')
        })
}

const getMarkerIcon = (messageType) => {
    switch (messageType) {
        case 2:
            return {icon: 'mdi-map-marker', color: '#FFD600'} // start - yellow
        case 3:
            return {icon: 'mdi-map-marker', color: 'black'}
        case 4:
            return {icon: 'mdi-flag-variant', color: 'red'} // destination - red flag
        default:
            return {icon: 'mdi-map-marker', color: 'black'}
    }
}
const addMarker = (details, active = false, animated = true, iconOverride = null, zIndexOffset = 0) => {
    let cssClass = 'opacity-0'; // hidden by default (for sequential animation later)
    if (active && animated) cssClass = 'drop'; // visible with drop animation
    else if (active) cssClass = ''; // visible immediately, no animation

    const markerIcon = iconOverride || getMarkerIcon(details.message_type);
    const marker = L.marker([details.latitude, details.longitude], {
        zIndexOffset,
        icon: L.divIcon({
            iconSize: [35, 35],
            className: "marker",
            html: `<i class="mdi ${markerIcon.icon} ${cssClass}" style="font-size: 18px; color: ${markerIcon.color}"/>`,
        }),
    });

    marker.bindPopup(buildPopupHtml(details));
    markers.value.push(marker);
    layerGroup.value?.addLayer(marker);
    return marker;
}

const addPolyline = (latLng, nextLatLng, strokeColor = '#4CAF50') => {
    const polyLine = L.polyline([latLng, nextLatLng], { color: strokeColor, weight: 5 });
    layerGroup.value?.addLayer(polyLine);
    polyLines.value.push(polyLine);
    return polyLine;
}

const addDot = (route) => {
    const dot = L.circleMarker([route.latitude, route.longitude], {
        radius: 3,
        color: getStrokeColor(route),
        weight: 1.5,
        fillColor: 'white',
        fillOpacity: 1,
    });
    dot.bindPopup(() => buildPopupHtml(route));
    layerGroup.value?.addLayer(dot);
}

// Draws the event-coloured overlay on the segment ENDING at `route` and
// places a tappable badge at that point. Adds the matching entry to the
// timeline panel. Safe to call when no events fired (no-op).
const addEventOverlay = (prevRoute, route) => {
    const bits = daEventBitsSet(route.da_event_flags);
    if (!bits.length || !layerGroup.value) return;

    const primary = primaryDaEvent(route.da_event_flags);
    const overlay = L.polyline(
        [[prevRoute.latitude, prevRoute.longitude],
         [route.latitude, route.longitude]],
        { color: primary.color, weight: 6, opacity: 0.9 },
    );
    layerGroup.value.addLayer(overlay);
    polyLines.value.push(overlay);

    // Badge: a small white circle stacking the event icons. Tapping it
    // opens the same per-point popup that the route marker uses, so
    // drivers/managers can see speed/voltage/timestamp at the event.
    const iconsHtml = bits
        .sort((a, b) => b.priority - a.priority)
        .map(b => `<i class="mdi ${b.icon}" style="color:${b.color};font-size:14px;line-height:1;"></i>`)
        .join('');
    const badge = L.marker([route.latitude, route.longitude], {
        zIndexOffset: 800, // above route markers (0-100), below replay (2000)
        icon: L.divIcon({
            className: 'da-event-badge',
            iconSize: [22 + (bits.length - 1) * 12, 22],
            iconAnchor: [(22 + (bits.length - 1) * 12) / 2, 11],
            html: `<div class="da-event-badge-inner" style="border:2px solid ${primary.color};">${iconsHtml}</div>`,
        }),
    });
    badge.bindPopup(buildPopupHtml(route));
    layerGroup.value.addLayer(badge);
    eventBadges.value.push(badge);

    events.value.push({
        index: events.value.length,
        route,
        bits,
        primary,
        latlng: [route.latitude, route.longitude],
        marker: badge,
    });
};

const buildPopupHtml = (details) => {
    const eventsLine = daEventLabels(details.da_event_flags);
    const primary = primaryDaEvent(details.da_event_flags);
    return `
        <ul class="v-list">
            <li>
                Location: <b>${details.latitude}, ${details.longitude}</b>
                <i class="mdi mdi-content-copy cursor-pointer" onclick="navigator.clipboard.writeText('${details.latitude}, ${details.longitude}')"></i>
            </li>
            <li>Accuracy: <b>${details.accuracy}</b></li>
            <li>Speed: <b>${strOperations().unitifySpeed(details)}</b></li>
            <li>Battery Voltage: <b>${strOperations().formatVoltage(details.voltage1)} / ${strOperations().formatVoltage(details.voltage2)}</b></li>
            <li>Reported At: <b>${formatDateTime(details.event_time)}</b></li>
            ${eventsLine ? `<li>Events: <b style="color: ${primary?.color || COLOR_DA_EVENT_FALLBACK}">${eventsLine}</b></li>` : ''}
            <li class="cursor-pointer font-weight-bold font-italic" onclick="window.open(\`http://maps.google.com/maps?q=${details.latitude},${details.longitude}&layer=t&cbll=${details.latitude},${details.longitude}&cbp=12,0,0,0,0&output=classic\`, '_blank')"
            ><i class="mdi mdi-google-street-view"></i>Streetview</li>
        </ul>
    `;
};

// Pan the map to a specific event and pop its badge.
const focusEvent = (event) => {
    if (!event || !map.value?.leafletObject) return;
    map.value.leafletObject.setView(event.latlng, Math.max(map.value.leafletObject.getZoom(), 16), {animate: true});
    if (event.marker) event.marker.openPopup();
};

const hideAllMarkers = () => {
    markers.value.forEach((marker, index) => {
        const markerElement = marker?.getElement();
        if (markerElement) {
            const iconElement = markerElement.querySelector("i");
            iconElement?.classList.add("opacity-0");
            iconElement?.classList.remove("drop");
        }
    });
}

const animateMarkers = () => {
    if (!markers.value.length) return;

    // Clear any existing animations
    clearAnimationTimeouts();

    const animationId = currentAnimationId.value;
    let animationCount = 0;

    const startAnimation = () => {
        // Check if this animation series is still valid
        if (animationId !== currentAnimationId.value) return;

        animationCount++;

        // Hide all markers first
        hideAllMarkers();

        // --- PER-MARKER TIMING ---
        const totalAnimationDuration = 5000; // ms
        const minimumTimeout = 20; // ms
        let perMarkerTimeout = Math.floor(totalAnimationDuration / markers.value.length);
        if (perMarkerTimeout < minimumTimeout) perMarkerTimeout = minimumTimeout;

        // Animate markers from START to END (index 0 to length-1)
        markers.value.forEach((marker, index) => {
            const timeoutId = setTimeout(() => {
                // Double-check if this animation is still valid
                if (animationId !== currentAnimationId.value) return;

                const markerElement = marker?.getElement();
                if (markerElement) {
                    const iconElement = markerElement.querySelector("i");
                    iconElement?.classList.remove("opacity-0");
                    iconElement?.classList.add("drop");
                }
            }, index * perMarkerTimeout); // Sequential timing: 0ms, 100ms, 200ms, etc.

            animationTimeouts.value.push(timeoutId);
        });

        // Schedule next animation cycle (repeat up to 3 times)
        if (animationCount < 3) {
            const nextCycleTimeout = setTimeout(() => {
                if (animationId === currentAnimationId.value) {
                    hideAllMarkers();
                    // Add small delay before starting next cycle
                    const restartTimeout = setTimeout(() => {
                        if (animationId === currentAnimationId.value) {
                            startAnimation();
                        }
                    }, 1500);
                    animationTimeouts.value.push(restartTimeout);
                }
            }, markers.value.length * perMarkerTimeout + 500); // Wait for all markers to finish + 500ms buffer

            animationTimeouts.value.push(nextCycleTimeout);
        }
    };

    startAnimation();
};

const resetRoutes = () => {
    // Stop replay and remove replay marker
    stopReplay();
    removeReplayMarker();

    // Clear animations first
    clearAnimationTimeouts();

    // Reset layer group
    layerGroup.value = null;

    if (map.value) {
        // Remove all layers except tile layer
        map.value.leafletObject.eachLayer((layer) => {
            if (!layer.hasOwnProperty('_url')) {
                map.value.leafletObject.removeLayer(layer);
            }
        });

        // Create new layer group
        layerGroup.value = L.featureGroup().addTo(map.value.leafletObject);
    }
    routes.value = [];
    markers.value = [];
    polyLines.value = [];
    eventBadges.value = [];
    events.value = [];
    showEventPanel.value = false;
    replayIndex.value = 0;
}

const setMapBounds = () => {
    if (map.value && layerGroup.value) {
        const bounds = layerGroup.value.getBounds();
        map.value.leafletObject.fitBounds(bounds, {padding: [50, 50]})
        map.value.leafletObject.panTo(bounds.getCenter());
    }
}

const mapTripRoutes = () => {
    const routesCount = routes.value.length;
    if (!routesCount) return;

    // Add markers and polylines in sequence
    if (map.value && layerGroup.value) {
        routes.value.forEach((route, index) => {
            if (route.latitude && !isNaN(route.latitude) && route.longitude && !isNaN(route.longitude)) {
                // Add polyline (connect current point to next point)
                if (index < routesCount - 1) {
                    const nextRoute = routes.value[index + 1];
                    if (nextRoute.latitude && nextRoute.longitude) {
                        addPolyline(
                            [route.latitude, route.longitude],
                            [nextRoute.latitude, nextRoute.longitude],
                            getStrokeColor(nextRoute)
                        );
                        // If the destination point reports any DA event,
                        // colour the segment by the highest-priority bit
                        // and add a tappable badge with all the matching
                        // event icons. Flags reset per emit, so the
                        // overlay only sits on the offending segment.
                        addEventOverlay(route, nextRoute);
                    }
                }

                if (index === 0) {
                    addMarker(route, false, true, {icon: 'mdi-play-circle', color: '#16A34A'}, 1000);
                } else if (index === routesCount - 1) {
                    addMarker(route, false, true, {icon: 'mdi-flag-variant', color: 'red'}, 1001);
                } else {
                    addDot(route);
                }
            }
        });
        setMapBounds();
    }
}

// --- Replay Logic ---
const togglePlayback = () => {
    if (isPlaying.value) {
        stopReplay();
    } else {
        startReplay();
    }
};

const startReplay = () => {
    if (!routes.value.length) return;

    // Force-stop initial animation and make all markers visible
    forceRevealAllMarkers();

    isPlaying.value = true;
    if (replayIndex.value >= routes.value.length - 1) {
        replayIndex.value = 0;
    }
    updateReplayPosition();
    scheduleNextStep();
};

const stopReplay = () => {
    isPlaying.value = false;
    if (replayTimer) {
        clearInterval(replayTimer);
        replayTimer = null;
    }
};

const scheduleNextStep = () => {
    if (replayTimer) clearInterval(replayTimer);
    const intervalMs = Math.max(50, 200 / replaySpeed.value);
    replayTimer = setInterval(() => {
        if (replayIndex.value < routes.value.length - 1) {
            replayIndex.value++;
            updateReplayPosition();
        } else {
            stopReplay();
        }
    }, intervalMs);
};

const seekTo = (index) => {
    // Force-stop initial animation and make all markers visible
    forceRevealAllMarkers();

    replayIndex.value = index;
    updateReplayPosition();
    // If playing, restart the interval from the new position
    if (isPlaying.value) {
        scheduleNextStep();
    }
};

const cycleSpeed = () => {
    const idx = SPEED_OPTIONS.indexOf(replaySpeed.value);
    replaySpeed.value = SPEED_OPTIONS[(idx + 1) % SPEED_OPTIONS.length];
    if (isPlaying.value) {
        scheduleNextStep();
    }
};

const updateReplayPosition = () => {
    const route = routes.value[replayIndex.value];
    if (!route || !map.value?.leafletObject) return;

    const lat = parseFloat(route.latitude);
    const lng = parseFloat(route.longitude);
    if (isNaN(lat) || isNaN(lng)) return;

    const heading = parseFloat(route.heading) || 0;
    const speed = strOperations().unitifySpeed(route);

    if (!replayMarker) {
        replayMarker = L.marker([lat, lng], {
            zIndexOffset: 2000,
            icon: L.divIcon({
                iconSize: [24, 24],
                iconAnchor: [12, 12],
                className: 'replay-marker',
                html: `<div class="replay-arrow" style="transform: rotate(${heading}deg)">
                    <i class="mdi mdi-navigation" style="font-size: 20px; color: #1565C0;"></i>
                </div>`,
            }),
        });
        replayMarker.bindPopup(`Speed: <b>${speed}</b><br>Time: <b>${formatDateTime(route.event_time)}</b>`);
        layerGroup.value?.addLayer(replayMarker);
    } else {
        replayMarker.setLatLng([lat, lng]);
        // Update heading via DOM to avoid expensive setIcon on every step
        const el = replayMarker.getElement();
        const arrow = el?.querySelector('.replay-arrow');
        if (arrow) arrow.style.transform = `rotate(${heading}deg)`;
        replayMarker.setPopupContent(`Speed: <b>${speed}</b><br>Time: <b>${formatDateTime(route.event_time)}</b>`);
    }

    const intervalMs = Math.max(50, 200 / replaySpeed.value);
    const panDuration = Math.min(0.15, intervalMs / 1000);
    map.value.leafletObject.panTo([lat, lng], {animate: true, duration: panDuration});
};

const removeReplayMarker = () => {
    if (replayMarker) {
        replayMarker.remove();
        replayMarker = null;
    }
};

// Cleanup on unmount
onUnmounted(() => {
    clearAnimationTimeouts();
    stopReplay();
    removeReplayMarker();
});

watch(() => props.selectedDevice, async () => {
    resetRoutes();
}, {immediate: true});

watch(() => props.selectedTrip, async (trip) => {
    if (!trip || !mapReady.value) return;
    // Compare ids as strings. TripResource casts id to a string but the MQTT
    // trip payload carries the raw integer, and the MQTT handler below swaps
    // selectedTrip to that payload, so a strict compare read "different trip"
    // on every location packet. That threw away the polyline the MQTT handler
    // had just extended, reset the map bounds and killed any running replay.
    const changedTrip = String(trip.id ?? '') !== String(selectedTrip.value?.id ?? '');
    // update_count increments on every packet the gateway folds into the trip,
    // so it also covers a stitched trip and points delivered late by the spool.
    // The MQTT payload has no update_count, so only compare when both sides
    // actually carry one, otherwise this just reintroduces the redraw storm.
    const prevCount = selectedTrip.value?.update_count;
    const grew = trip.update_count !== undefined
        && prevCount !== undefined
        && trip.update_count !== prevCount;
    if (changedTrip || grew) {
        await drawTripRoutes();
    }
}, {deep: true, immediate: true});

watch(lastMqttUpdate, async (update) => {
    if (!update || !props.selectedDevice) return;
    if (String(update.slno) !== String(props.selectedDevice?.slno)) return;

    const newLocation = update.data;
    if (newLocation && newLocation.trip && parseInt(props.selectedTrip?.id) === parseInt(newLocation.trip.id)) {
        selectedTrip.value = newLocation.trip;
        const route = newLocation.trip_route;

        if (route.message_type === 2) {
            resetRoutes();
        }

        // Stop any running animation and make all existing markers visible
        forceRevealAllMarkers();
        stopReplay();
        removeReplayMarker();

        if (route.message_type === 2) {
            addMarker(route, true, false, {icon: 'mdi-play-circle', color: '#16A34A'}, 1000);
        } else {
            addMarker(route, true, false);
        }
        routes.value.push(route);

        if (routes.value.at(-2)) {
            const prev = routes.value.at(-2);
            addPolyline(
                [prev.latitude, prev.longitude],
                [route.latitude, route.longitude],
                getStrokeColor(route)
            );
            addEventOverlay(prev, route);
        }
        setMapBounds();
    }
});
</script>

<style>
@keyframes drop {
    0% {
        transform: translate(-8px, -30px);
        opacity: 0.7;
    }
    50% {
        transform: translate(-8px, -25px);
        opacity: 0.8;
    }
    65% {
        transform: translate(-8px, -20px);
        opacity: 0.9;
    }
    75% {
        transform: translate(-8px, -15px);
        opacity: 0.95;
    }
    100% {
        transform: translate(-9px, -2px);
        opacity: 1;
    }
}

.drop {
    position: absolute;
    animation: drop 1s ease-out forwards;
}

.opacity-0 {
    opacity: 0;
}

.replay-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fafafa;
    border-top: 1px solid #e0e0e0;
    padding: 4px 8px;
}

.replay-slider {
    flex: 1;
    min-width: 0;
}

.speed-btn {
    min-width: 42px !important;
    font-size: 11px;
    font-weight: 600;
}

.replay-marker {
    background: none !important;
    border: none !important;
}

.replay-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.4));
}

/* Driver-analytics event badge - anchored on the event's destination
 * point. Tapping pops the same per-point detail popup. */
.da-event-badge {
    background: none !important;
    border: none !important;
}
.da-event-badge-inner {
    background: #ffffff;
    border-radius: 999px;
    padding: 2px 6px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
    display: inline-flex;
    align-items: center;
    gap: 2px;
}

/* Floating timeline panel - top-right of the map. Collapsed by default;
 * the toggle pill shows the event count, expanded shows a scrollable
 * chronological list. */
.da-event-panel {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 500;
    background: rgba(255, 255, 255, 0.96);
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    max-width: 240px;
    overflow: hidden;
}
.da-event-panel-toggle {
    border: none;
    background: transparent;
    width: 100%;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}
.da-event-panel-count {
    margin: 0 4px;
    color: #333;
}
.da-event-panel-list {
    max-height: 220px;
    overflow-y: auto;
    border-top: 1px solid #eee;
}
.da-event-panel-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    border-bottom: 1px solid #f4f4f4;
    cursor: pointer;
    font-size: 12px;
}
.da-event-panel-row:last-child {
    border-bottom: none;
}
.da-event-panel-row:hover {
    background: #f5f5f5;
}
.da-event-panel-row-icons {
    display: inline-flex;
    gap: 2px;
    font-size: 16px;
    line-height: 1;
}
.da-event-panel-row-meta {
    flex: 1;
    min-width: 0;
}
.da-event-panel-row-labels {
    font-weight: 600;
    color: #222;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.da-event-panel-row-time {
    color: #666;
    font-size: 11px;
}
</style>
